import { Link } from 'react-router-dom';
import type { MovieItem } from '@/mocks/movies';
import { categoryNames } from '@/mocks/movies';

interface MovieCardProps {
  movie: MovieItem;
  variant?: 'default' | 'compact' | 'wide';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
          <img
            src={movie.image}
            alt={`${movie.title} 海报`}
            title={`${movie.title} - 牛牛影视高清在线观看`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
          <div className="absolute top-2 left-2">
            <span className="px-1.5 py-0.5 text-[10px] bg-primary-500 text-background-950 rounded font-semibold">
              {movie.rating}
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h3 className="text-sm font-semibold text-white leading-tight line-clamp-2">
              {movie.title}
            </h3>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group flex gap-4 bg-background-100 rounded-lg overflow-hidden cursor-pointer hover:bg-background-200 transition-colors"
      >
        <div className="w-32 sm:w-40 flex-shrink-0 relative overflow-hidden bg-background-200">
          <img
            src={movie.image}
            alt={`${movie.title} 海报`}
            title={`${movie.title} - 牛牛影视高清在线观看`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 py-3 pr-4 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors">
              {movie.title}
            </h3>
            <span className="text-xs px-1.5 py-0.5 rounded bg-accent-500/20 text-accent-400">
              {movie.rating}
            </span>
          </div>
          <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2">
            {movie.description}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {movie.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-[10px] px-1.5 py-0.5 rounded bg-background-200 text-foreground-500"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-foreground-600">
            {movie.updateInfo}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-200">
        <img
          src={movie.image}
          alt={`${movie.title} 海报`}
          title={`${movie.title} - 牛牛影视高清在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2 py-0.5 text-xs bg-primary-500 text-background-950 rounded-md font-semibold">
            {movie.rating}
          </span>
          {movie.isNew && (
            <span className="px-2 py-0.5 text-xs bg-accent-500 text-background-950 rounded-md font-semibold">
              NEW
            </span>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-base font-semibold text-white mb-1 line-clamp-1">
            {movie.title}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-300">{movie.year}</span>
            <span className="text-xs text-foreground-500">
              {categoryNames[movie.category] || movie.category}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}